import { SetStateAction, useState } from "react";
import isEqual from "lodash-es/isEqual";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { AirFilterType, SeaFilterType } from "features/shared/filter.types";
import { AirOrderQuery } from "entities/AirSavedCard";
import { AirWhereQuery } from "entities/AirSavedCard";
import { usePrevious } from "shared/hooks/usePrevious.hook";
type UseAirFilterContextReturnType = {
  airFilter: AirFilterType;
  airFilterInnerValues: AirFilterType;
  setAirInnerWhereQuery: (valueAction: SetStateAction<AirWhereQuery>) => void;
  setAirInnerOrderQuery: (valueAction: SetStateAction<AirOrderQuery[]>) => void;
  resetAirInnerFilter: () => void;
  saveAirFilter: (filterValue: AirFilterType) => void;
  resetAirFilter: () => void;
  airFilterChanged: () => boolean;
  isAirFilter: (filter: AirFilterType | SeaFilterType) => filter is AirFilterType;
};

export const useAirFilterContext = (): UseAirFilterContextReturnType => {
  const [airFilter, setAirFilter] = useState<AirFilterType>({
    where: {},
    order: [],
    filterType: ShipmentType.AIR,
  });
  const [airFilterInnerValues, setAirFilterInnerValues] = useState<AirFilterType>(airFilter);

  const previousAirFilter = usePrevious<AirFilterType>(airFilter);

  const setAirInnerWhereQuery = (valueAction: SetStateAction<AirWhereQuery>) => {
    setAirFilterInnerValues((prev: AirFilterType) => {
      return {
        ...prev,
        where: typeof valueAction === "function" ? valueAction(prev.where) : valueAction,
      };
    });
  };

  const setAirInnerOrderQuery = (valueAction: SetStateAction<AirOrderQuery[]>) => {
    setAirFilterInnerValues((prev: AirFilterType) => {
      return {
        ...prev,
        order: typeof valueAction === "function" ? valueAction(prev.order) : valueAction,
      };
    });
  };

  const resetAirInnerFilter = () => {
    setAirFilterInnerValues({ where: {}, order: [], filterType: ShipmentType.AIR });
  };

  const saveAirFilter = (filterValue: AirFilterType) => {
    setAirFilter(filterValue);
  };

  const resetAirFilter = () => {
    setAirFilter({ where: {}, order: [], filterType: ShipmentType.AIR });
    resetAirInnerFilter();
  };

  const airFilterChanged = (): boolean => {
    if (!previousAirFilter) return false;
    const isPreviousAirFilterEmpty =
      Object.keys(previousAirFilter.where).length === 0 && previousAirFilter.order.length === 0;
    if (isPreviousAirFilterEmpty) return false;

    const whereChanged = !isEqual(previousAirFilter.where, airFilter.where);
    const orderChanged = !isEqual(previousAirFilter.order, airFilter.order);

    return whereChanged || orderChanged;
  };

  return {
    airFilter,
    airFilterInnerValues,
    setAirInnerWhereQuery,
    setAirInnerOrderQuery,
    resetAirInnerFilter,
    saveAirFilter,
    resetAirFilter,
    airFilterChanged,
    isAirFilter: (filter): filter is AirFilterType => filter.filterType === ShipmentType.AIR,
  };
};
